import { BookmarkIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Container } from '@mui/material';
import BackButton from '@straico-ui/commons/BackButton';
import SmartSwitchInput from '@straico-ui/forms/SmartSwitchInput';
import LabelIcon from '@straico-ui/forms/LabelIcon';
import TextInput from '@straico-ui/forms/TextInput';
import useChatStore from 'Components/Chat/store';
import PercentageHandler from 'Components/Common/PercentageHandler/PercentageHandler';
import LLMCombos from 'Components/Forms/LLMCombos';
import { inject, observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DraggableList from 'react-draggable-list';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import useThemeStore from 'Theme/store';
import { cn } from 'utils/styles';
import * as yup from 'yup';
import ModelsModal from '../Features/ModelsModal';
import ComboItemTemplate from './ComboItemTemplate';

const DefaultLLMsAndWordLimits = ({ store, onSubmitSuccess }) => {
  /** THEME */
  const { theme } = useThemeStore();

  /** NAVIGATION */
  const navigate = useNavigate();

  /** PROFILE INFO */
  const { default_model, multi_default_models, wordcap_limits } = store.profile;

  /** STATES */
  const wordcapLimitsSection = useRef(null);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [pricingMethod, setPricingMethod] = useState(
    store.profile.pricing_method
  );
  const [wordcapLimits, setWordcapLimits] = useState(
    getLimitValues(wordcap_limits)
  );
  const [modelsDialogOpen, setModelsDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    selectedModels: [],
    handleSelectModels: () => {},
  });
  const [newlyCreatedComboId, setNewlyCreatedComboId] = useState(null);

  const {
    combos,
    fetchCombos,
    updateCombo,
    selectedCombo,
    setSelectedCombo,
    updateSelectedModels,
  } = useChatStore();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      default_model: [default_model],
      multi_default_models:
        multi_default_models && Array.isArray(multi_default_models) && multi_default_models.length > 0
          ? multi_default_models
          : [default_model],
      wordcap_limits: wordcap_limits,
    },
    resolver: yupResolver(schemaValidation),
  });

  const viabilityMultiDefault = (multi_default_models) => {
    // Filter models based on IDs in multi_default_models
    const defaultModels = models.filter((model) =>
      multi_default_models.includes(model.id)
    );

    // Filter default models that are enabled
    const enabledModels = defaultModels.filter((model) => model.enabled);

    if (enabledModels.length > 0) {
      return enabledModels.map((model) => model.id);
    } else {
      // If no enabled default models, fallback based on conditions
      if (!default_model.enabled) {
        if (store.profile.plan === 'free') {
          return ['65f377dcaa4feb73a4ae62ac'];
        } else {
          return ['6620346cecf94d7b874019f2'];
        }
      } else {
        return [default_model.id];
      }
    }
  };

  useEffect(() => {
    if (!combos.length) {
      fetchCombos();
    }
  }, [combos, fetchCombos]);

  const onSubmit = async (data) => {
    setSubmitLoading(true);
    try {
      const res = await store.api.post(`/user/update-user`, {
        default_model: data.default_model,
        multi_default_models: data.multi_default_models,
        wordcap_limits: getValues('wordcap_limits'),
        pricing_method: pricingMethod,
      });
      localStorage.removeItem('currentModel');

      if (res.status === 200) {
        localStorage.setItem('profile', JSON.stringify(res.data));
        store.profile.pricing_method = res.data.pricing_method;
        store.profile.wordcap_limits = res.data.wordcap_limits;
        toast.success('Profile updated');
        if (onSubmitSuccess) onSubmitSuccess();
      } else {
        toast.error('Updating error');
      }
    } catch (error) {
      toast.error('Updating error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const changeLimits = (newValues) => {
    const currentValues = getValues('wordcap_limits');
    setWordcapLimits(newValues);
    setValue('wordcap_limits', { ...currentValues, ...newValues });
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await store.api.get(`/model`);
        let availableModels = response.data.models.filter(
          (model) => model.enabled
        );
        let transformedModels;
        if (store.profile.plan == 'freeTos') {
          const res = await store.api.get('/user/constant', {
            params: {
              type: 'princing_plans',
            },
          });
          let freePlanModels = []; // Array to store models allowed for free users
          freePlanModels = res.data.rules[0].models;
          const modelsInFreePlan = []; // Array to store models available for free users
          const otherModels = []; // Array to store models not available for free users
          availableModels.forEach((model) => {
            if (freePlanModels.includes(model.model)) {
              modelsInFreePlan.push(model);
            } else {
              otherModels.push(model);
            }
          });
          transformedModels = modelsInFreePlan.map((model) => ({
            id: model._id,
            title: model.name,
          }));
        } else {
          transformedModels = response.data.models.map((model) => ({
            id: model._id,
            title: model.name,
          }));
        }
        control.multi_default_models =
          viabilityMultiDefault(multi_default_models);
        setModels(transformedModels);
        setLoadingModels(false);
      } catch (error) {
        console.log(error);
      }
    })();
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const keys = Object.keys(errors);
      keys.forEach((key) => {
        toast.error(errors[key].message);
      });
    }
  }, [errors]);

  const [combosList, setCombosList] = useState([]);

  const [allModels, setAllModels] = useState([]);
  useEffect(() => {
    const fetchAllModels = async () => {
      try {
        const response = await store.api.get(`/model`);
        setAllModels(response.data.models || []);
      } catch (error) {
        console.error('Error fetching all models:', error);
      }
    };

    fetchAllModels();
  }, []);

  useEffect(() => {
    if (!combos.length) {
      fetchCombos();
    } else {
      const formattedCombos = combos.map((combo) => ({
        id: combo._id,
        name: combo.name,
        llms: combo.llms,
        position: combo.position,
        onSelect: () => updateCombo(combo._id),
        store,
        theme,
        allModels,
      }));
      setCombosList(formattedCombos);
    }
  }, [combos]);

  const handleMoveEnd = useCallback(
    async (newList) => {
      const updatedList = newList.map((item, index) => ({
        ...item,
        position: index + 1,
        isDefault: index === 0,
      }));

      setCombosList(updatedList);
      try {
        // Helper function to retry API calls with exponential backoff
        const retryApiCall = async (apiCall, maxRetries = 3) => {
          let lastError;
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
              return await apiCall();
            } catch (error) {
              console.warn(
                `API call failed (attempt ${attempt + 1}/${maxRetries}):`,
                error
              );
              lastError = error;
              // Exponential backoff: wait longer between each retry
              await new Promise((resolve) =>
                setTimeout(resolve, 300 * Math.pow(2, attempt))
              );
            }
          }
          throw lastError; // If we've exhausted all retries, throw the last error
        };

        // Update the backend with retry logic
        await Promise.all(
          updatedList.map((item) =>
            retryApiCall(() =>
              store.api.put(`/combos/llm-combos/${item.id}`, {
                name: item.name,
                llms: item.llms,
                isDefault: item.isDefault,
                position: item.position,
                isSelected: item.isSelected,
              })
            )
          )
        );

        // Add a small delay to ensure backend updates are processed before fetching
        await new Promise((resolve) => setTimeout(resolve, 500));
        await fetchCombos();

        const firstCombo = updatedList[0];
        if (firstCombo) {
          setSelectedCombo(firstCombo);

          const selectedModels = firstCombo.llms
            .map((llmId) => allModels.find((model) => model._id === llmId))
            .filter(Boolean)
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

          updateSelectedModels(() => selectedModels);
          localStorage.setItem('currentModels', JSON.stringify(selectedModels));
        }
      } catch (error) {
        console.error('Error updating combo positions and models:', error);
        toast.error('Failed to update combo positions. Please try again.');
      }
    },
    [
      store,
      fetchCombos,
      allModels,
      updateSelectedModels,
      setCombosList,
      setSelectedCombo,
    ]
  );

  const memoizedCombosList = useMemo(() => {
    // Make a copy and ensure all positions are valid
    const validatedCombos = [...combosList].map((combo) => {
      // Ensure position is a number
      return {
        ...combo,
        position: typeof combo.position === 'number' ? combo.position : 999,
      };
    });

    // Sort by position and re-assign sequential positions
    return validatedCombos
      .sort((a, b) => a.position - b.position)
      .map((item, index) => ({
        ...item,
        position: index + 1,
        isDefault: index === 0,
      }));
  }, [combosList]);

  const openModelsDialog = useCallback(
    ({ selectedModels, handleSelectModels }) => {
      setDialogData({ selectedModels, handleSelectModels });
      setModelsDialogOpen(true);
    },
    []
  );

  const cloneCombo = useCallback(
    async ({ baseName, validModels, combos }) => {
      let newName = `${baseName}`;
      let copyNumber = 2;
      while (
        combos.some(
          (combo) => combo.name.toLowerCase() === newName.toLowerCase()
        )
      ) {
        newName = `${baseName} (copy ${copyNumber})`;
        copyNumber++;
      }

      try {
        // 1) Crear la copia en el backend
        const newCombo = await store.api.post('/combos/llm-combos', {
          name: newName,
          llms: validModels.map((model) => model._id),
          isDefault: false,
          isSelected: true,
          isStraicoDefault: false,
        });

        const createdCombo = newCombo.data.combo;

        // Verificar que el _id del clon sea único
        if (combos.some((combo) => combo._id === createdCombo._id)) {
          console.warn(
            '[cloneCombo] Advertencia: El _id del combo clonado ya existe en la lista.'
          );
        }

        // 2) Agregar el nuevo combo al estado global
        useChatStore.setState((state) => ({
          combos: [...state.combos, createdCombo],
        }));
        setSelectedCombo(createdCombo);

        // 3) Notificar que este combo es el recién creado para edición
        setNewlyCreatedComboId(createdCombo._id);

        return createdCombo;
      } catch (error) {
        console.error('[cloneCombo] Error creando copia del combo:', error);
        toast.error('Failed to create combo copy.');
        return null;
      }
    },
    [store]
  );

  const memoizedTemplate = useCallback(
    (templateProps) => {
      const { item } = templateProps;
      return (
        <ComboItemTemplate
          {...templateProps}
          allModels={allModels}
          openModelsDialog={openModelsDialog}
          onClone={cloneCombo}
          startEditing={item.id === newlyCreatedComboId}
        />
      );
    },
    [allModels, openModelsDialog, newlyCreatedComboId, cloneCombo]
  );

  const closeModelsDialog = useCallback(() => {
    setModelsDialogOpen(false);
  }, []);

  return (
    <div className="overflow-auto">
    <Container maxWidth="md" className="font-figtree">
      <BackButton
          text="Go back to user settings"
          onClick={() => navigate('/my-profile')}
        />
        <div
          className={cn('my-4 mb-10', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}
        >
          <h1 className={cn('text-3xl font-bold', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}>LLMs and Word Limits</h1>
          <p className={cn('text-xl mt-2', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}>
            Configure your default AI models and word limits.
          </p>
        </div>
        <form
          className="mx-auto flex flex-col justify-between space-y-6"
          onSubmit={handleSubmit(onSubmit)}
        >
        <section
          id="personalizeAI"
          className="flex flex-col gap-6 p-5 rounded-xl border border-gray-500/50"
        >
          <p className={cn("text-2xl font-bold capitalize", {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}>
            Personalize Your AI Interactions
          </p>
          <div className="flex flex-col gap-3">
            <p className={cn("font-bold capitalize", {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}>Default AI Models</p>
            <p className={cn("text-sm", {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}>
              Your interactions with AI should be as individual as you are.
              Create LLM combinations with{' '}
              <strong>up to 4 default Large Language Models (LLMs)</strong>,
              crafting a personalized path of discovery and interaction for all
              your chats.
            </p>
            <p className={cn("text-sm", {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}>
              If you want to modify, create, or delete any combo, you can do so
              in the section below. To set the default combo that loads when you
              start a new chat, simply{' '}
              <strong>drag your preferred option to the top of the list</strong>
              — it'll be automatically ready for you every time you enter.
            </p>

            <LLMCombos
              store={store}
              combos={combos}
              selectedCombo={selectedCombo}
              onSelectCombo={updateCombo}
              theme={theme}
              allModels={allModels}
            />
            <div style={{ position: 'relative' }}>
              {' '}
              {combosList.length > 0 ? (
                <DraggableList
                  list={memoizedCombosList}
                  itemKey="id"
                  template={memoizedTemplate}
                  onMoveEnd={handleMoveEnd}
                  padding={10}
                  bounds="parent"
                  constrainDrag={true}
                />
              ) : (
                <p>No combos available</p>
              )}
            </div>
          </div>
          {/* Pricing method description section */}
          <div className="flex flex-col gap-3">
            <p id="pricing-settings" className={cn("font-bold capitalize", {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}>
              Pricing mode
            </p>
            <div className="flex items-center gap-2">
              <div className="w-4/5">
                <div className="flex items-center gap-4">
                  <span
                    className={cn("text-sm", {
                      'text-white': theme === 'dark',
                      'text-[#211E21]': theme !== 'dark',
                    })}
                  >
                    Pricing per Message
                  </span>

                  <SmartSwitchInput
                    checked={pricingMethod === 'perWord'}
                    onChange={(event) => {
                      const newMethod = event.target.checked
                        ? 'perWord'
                        : 'perMessage';
                      setPricingMethod(newMethod);
                      setValue('pricing_method', newMethod);
                      store.profile.pricing_method = newMethod;
                    }}
                  />

                  <span
                    className={cn("text-sm", {
                      'text-white': theme === 'dark',
                      'text-[#211E21]': theme !== 'dark',
                    })}
                  >
                    Pricing per Word
                  </span>
                </div>
              </div>
            </div>
            <div className={cn("mt-2 text-sm", {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}>
              {pricingMethod === 'perMessage' ? (
                <p>
                  <strong>Pricing per Message</strong>: Every interaction
                  deducts coins based on the length of your prompt and response,
                  but with an upper cap. This ensures predictable pricing, even
                  in long conversations where word accumulation previously
                  caused costs to escalate dramatically.{' '}
                  <a
                    href="https://straico.com/pricing-modes/"
                    className="text-violet-blue cursor-pointer"
                  >
                    Check this article
                  </a>{' '}
                  for more information.
                </p>
              ) : (
                <div className="space-y-2">
                  <p>
                    <strong>Per Word pricing</strong>: Coins will be deducted
                    depending on the number of words of the prompt, output,
                    conversation and context. You'll see color-coded alerts to
                    help you understand prompt costs at a glance:
                  </p>
                  <ul className="list-disc ml-5 mt-2">
                    <li>
                      <span className="text-[#9cb773] font-semibold">
                        Green
                      </span>
                      : Low cost (under 60 coins)
                    </li>
                    <li>
                      <span className="text-[#e3c06f] font-semibold">
                        Yellow
                      </span>
                      : Moderate cost (60-199 coins)
                    </li>
                    <li>
                      <span className="text-[#e68e5b] font-semibold">
                        Orange
                      </span>
                      : High cost (200+ coins)
                    </li>
                  </ul>
                  <p>
                    <a
                      href="https://straico.com/pricing-modes/"
                      className="text-violet-blue cursor-pointer"
                    >
                      Check this article
                    </a>{' '}
                    for more information.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className={cn("font-bold capitalize", {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}>Wordcap Limit</p>
            <p className={cn("text-sm", {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}>
              Manage{' '}
              <strong>how many words are included in your requests</strong> to
              optimize your Straico coin usage. Recommended limit is 1,000 words
              per <strong>request</strong>. If you set the limit to 0, the
              wordcap will be disabled, and word capacity will then depend on
              the model's specific limits.
            </p>
            <div className="flex items-center w-full">
              <div className="w-1/5 mr-2">
                <LabelIcon icon={CalculatorIcon} text="Words Per Request" />
              </div>
              <div className="w-4/5">
                <div className="w-fit">
                  <TextInput
                    {...register('wordcap_limits.words')}
                    placeholder="1000"
                    type="number"
                    name="wordcap_limits.words"
                    min={0}
                  />
                </div>
              </div>
            </div>
          </div>
          <p className={cn('', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}>
            Use the slider below to configure the percentage distribution for
            each component of the Wordcap Limit:
          </p>
          <PercentageHandler
            attributes={wordcapLimits}
            words={getValues('wordcap_limits').words}
            onChange={changeLimits}
          />
        </section>
        <div className="w-full pt-3">
          <LoadingButton
            variant="contained"
            color="primary"
            type="submit"
            loading={submitLoading}
            startIcon={<BookmarkIcon className="size-6" />}
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: '18px',
              textTransform: 'initial',
            }}
          >
            Save changes
          </LoadingButton>
          <div className="h-10" />
        </div>
      </form>
      {modelsDialogOpen && (
        <ModelsModal
          open={modelsDialogOpen}
          selectedModels={dialogData.selectedModels}
          handleCombos={dialogData.handleSelectModels}
          onClose={closeModelsDialog}
          nocapabilities={true}
        />
      )}
      </Container>
    </div>
  );
};

const schemaValidation = yup.object({});

const getLimitValues = (values) => {
  const temp = JSON.parse(JSON.stringify(values));
  delete temp.words;
  return temp;
};

export default inject('store')(observer(DefaultLLMsAndWordLimits));
